import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import SlidePanel from '../../shared/SlidePanel';
import { Icon } from '../BaileyBits';
import { when } from '../bailey/format';
import { hapticTick } from '../../../_utils/haptics';
import { OUTCOMES, rowMeta, readerMeta, timeOfDay } from './transcript';
import { safeComponents } from '../bailey/skills/safeMarkdown';

/** What happened, as a small pill: Booked, Link sent, Message, Answered. */
export function OutcomePill({ outcome }) {
    const o = OUTCOMES[outcome];
    if (!o) return null;
    return <span className={`fd-pill is-${o.tone}`}>{o.tone === 'done' && <Icon.Tick size={11} />}{o.label}</span>;
}

/** One conversation in a list: channel, title, what happened, who, when. */
export function ConversationRow({ c, onOpen, timeOnly = false }) {
    return (
        <button type="button" className="fd-conv" onClick={() => { hapticTick(); onOpen(c); }}>
            <span className="fd-conv-ch" aria-hidden="true">{c.channel === 'phone' ? <Icon.Phone size={16} /> : <Icon.Chat size={16} />}</span>
            <span className="fd-conv-main">
                <b>{c.title}</b>
                <span className="fd-conv-sub"><OutcomePill outcome={c.outcome} /><small>{rowMeta(c)}</small></span>
            </span>
            <small className="fd-conv-time">{timeOnly ? timeOfDay(c.startedAt || c.at) : when(c.startedAt || c.at)}</small>
            <span className="bly-chev" aria-hidden="true"><Icon.Chevron /></span>
        </button>
    );
}

const EVENT_ICONS = { clock: Icon.Clock, tag: Icon.Tag, link: Icon.Link, tick: Icon.Tick, info: Icon.Info, chat: Icon.Chat, phone: Icon.Phone };

// Bailey's words, formatted as the client saw them. Beu's own links open in a
// new tab; any other address is shown, not linked, and pictures never load
// (the client's own words are in this conversation too).
const mdComponents = safeComponents();

/** One conversation Bailey had with a client, in full. */
export function ConversationPanel({ conversation: c, onClose, onTeach }) {
    return (
        <SlidePanel title={c.title} onClose={onClose} width={480} className="bly-panel fd-reader">
            <div className="bly-panel-body fd-transcript">
                <Transcript conversation={c} onTeach={onTeach} />
            </div>
        </SlidePanel>
    );
}

/** What was said, word for word, with the steps Bailey took between. */
export function Transcript({ conversation: c, onTeach }) {
    let prev = null;
    return (
        <>
            <p className="fd-transcript-meta">{readerMeta(c)}</p>
            {c.summary && (
                <div className="fd-summary">
                    <OutcomePill outcome={c.outcome} />
                    <p>{c.summary}</p>
                </div>
            )}
            {!c.turns.length && <p className="bly-panel-note">Nothing was said in this one.</p>}
            <div className="fd-thread">
                {c.turns.map((t, i) => {
                    const out = [];
                    if (t.time) out.push(<p key={`t${i}`} className="fd-thread-time">{timeOfDay(t.time)}</p>);
                    if (t.kind === 'event') {
                        const I = EVENT_ICONS[t.icon] || Icon.Info;
                        out.push(<p key={i} className="fd-ev"><I size={14} /><span>{t.text}</span></p>);
                    } else {
                        // The name shows when the speaker changes, not on every line.
                        const first = prev !== t.kind || !!t.time;
                        const who = t.kind === 'client' ? (c.clientName || 'Client') : 'Bailey';
                        out.push(
                            <div key={i} className={`fd-turn is-${t.kind}${first ? ' is-first' : ''}`}>
                                {first && <span className="fd-turn-who">{who}</span>}
                                {t.kind === 'bailey'
                                    ? <div className="fd-bub bly-md"><ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{t.text}</ReactMarkdown></div>
                                    : <div className="fd-bub">{t.text}</div>}
                            </div>
                        );
                        prev = t.kind;
                    }
                    return out;
                })}
            </div>
            {onTeach && (
                <div className="fd-teach">
                    <p>Did I get something wrong? Add an answer and I’ll use it next time.</p>
                    <button type="button" className="bly-btn is-secondary" onClick={() => { hapticTick(); onTeach(); }}>Add an answer</button>
                </div>
            )}
        </>
    );
}
